'use client'

import type { GraphQLResult } from 'aws-amplify/api'
import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { getAmplifyClient } from '@/services/amplifyClient'
import { getSelectedTeamSessionScopeKey } from '@/app/actions/team'
import {
  assertSupportReadCurrent,
  assertSupportWorkspace,
  readSupportPayload,
  supportErrorCode,
  SupportRequestError,
  type SupportTarget,
  type SupportWorkspace,
  supportWorkspaceQuery,
} from './supportContract'

export function useSupportTarget(): SupportTarget | null {
  const runtime = useSessionRuntime()
  if (
    !runtime.isImpersonating ||
    !runtime.currentUserId ||
    !runtime.impersonatedUserId
  )
    return null
  return {
    actorUserID: runtime.currentUserId,
    targetUserID: runtime.impersonatedUserId,
    sessionScopeKey: runtime.sessionScopeKey,
  }
}

export async function fetchSupportPayload<T>(
  query: string,
  field: string,
  variables: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  assertSupportReadCurrent(signal)
  try {
    const result = (await getAmplifyClient().graphql({
      query,
      variables,
    })) as GraphQLResult<Record<string, { payload?: unknown } | null>>
    assertSupportReadCurrent(signal)
    return readSupportPayload<T>(result, field)
  } catch (error) {
    assertSupportReadCurrent(signal)
    if (error instanceof SupportRequestError) throw error
    throw new SupportRequestError(supportErrorCode(error))
  }
}

export async function fetchSupportWorkspace(
  target: SupportTarget,
  organizationID?: string,
  signal?: AbortSignal,
) {
  const workspace = await fetchSupportPayload<SupportWorkspace>(
    supportWorkspaceQuery,
    'consoleSupportWorkspace',
    { targetUserID: target.targetUserID, organizationID },
    signal,
  )
  assertSupportWorkspace(workspace, target.actorUserID, target.targetUserID)
  return workspace
}

export type SupportCommand =
  | 'CreateProject'
  | 'UpdateProject'
  | 'CreateOrganizationMember'
  | 'UpdateOrganizationMember'
  | 'AssignSeat'
  | 'ReassignSeat'

const SUPPORT_COMMAND_INPUT_TYPES: Record<SupportCommand, string> = {
  CreateProject: 'SupportCreateProjectInput',
  UpdateProject: 'SupportUpdateProjectInput',
  CreateOrganizationMember: 'SupportCreateOrganizationMemberInput',
  UpdateOrganizationMember: 'UpdateOrganizationMemberInput',
  AssignSeat: 'AssignSeatInputInput',
  ReassignSeat: 'ReassignSeatInputInput',
}

/** Finite commands only; no caller-controlled GraphQL, identity or backend function. */
export async function runSupportCommand<T>(
  command: SupportCommand,
  target: SupportTarget,
  organizationID: string,
  input: Record<string, unknown>,
): Promise<T> {
  if (!organizationID) throw new Error('Support organization scope is required')
  if ((await getSelectedTeamSessionScopeKey()) !== target.sessionScopeKey)
    throw new Error('Support session scope changed')
  const field = `consoleSupport${command}`
  return fetchSupportPayload<T>(
    `mutation ConsoleSupport${command}($targetUserID: ID!, $organizationID: ID!, $input: ${SUPPORT_COMMAND_INPUT_TYPES[command]}!) {
    ${field}(targetUserID: $targetUserID, organizationID: $organizationID, input: $input) { payload }
  }`,
    field,
    {
      targetUserID: target.targetUserID,
      organizationID,
      input,
    },
  )
}
